// Orders.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './orders.css';

function Orders() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const [orders, setOrders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [activeTab, setActiveTab] = useState('all');
	const [expandedShipping, setExpandedShipping] = useState(null);
	const [confirmingOrderId, setConfirmingOrderId] = useState(null);

	const tabs = [
		{ key: 'all', label: '전체', status: null },
		{ key: 'pending', label: '결제대기', status: 'pending' },
		{ key: 'paid', label: '결제완료', status: 'paid' },
		{ key: 'preparing', label: '배송준비', status: 'preparing' },
		{ key: 'shipping', label: '배송중', status: 'shipping' },
		{ key: 'delivered', label: '배송완료', status: 'delivered' },
		{ key: 'confirmed', label: '구매확정', status: 'confirmed' },
		{ key: 'cancelled', label: '취소/환불', status: 'cancelled' }
	];

	const getImageUrl = (url) => {
		if (!url) return 'https://via.placeholder.com/80';
		if (url.startsWith('/media')) {
			return `https://api.weartoday.kr${url}`;
		}
		return url;
	};

	useEffect(() => {
		const status = searchParams.get('status');
		if (status) {
			const tab = tabs.find(t => t.status === status);
			if (tab) setActiveTab(tab.key);
		}
		fetchOrders();
	}, [searchParams]);

	const fetchOrders = async () => {
		setLoading(true);
		try {
			const token = localStorage.getItem('access_token');
			const response = await axios.get(
				'https://api.weartoday.kr/api/orders/',
				{
					headers: {
						'Authorization': `Bearer ${token}`
					}
				}
			);

			console.log('✅ 주문내역 응답:', response.data);
			setOrders(response.data.results || response.data || []);

		} catch (error) {
			console.error('❌ 주문내역 로딩 실패:', error);
			if (error.response?.status === 401) {
				alert('로그인이 만료되었습니다.');
				navigate('/login');
			}
		} finally {
			setLoading(false);
		}
	};

	// ⭐ 구매확정 처리
	const handleConfirmOrder = async (orderId) => {
		if (!window.confirm('구매확정 하시겠습니까?\n\n확정 후에는 환불/교환이 어려울 수 있습니다.')) {
			return;
		}

		setConfirmingOrderId(orderId);
		try {
			const token = localStorage.getItem('access_token');
			const response = await axios.post(
				`https://api.weartoday.kr/api/orders/${orderId}/confirm/`,
				{},
				{
					headers: {
						'Authorization': `Bearer ${token}`
					}
				}
			);

			alert(`🎉 구매확정 완료!\n\n${response.data.earned_point}P 적립되었습니다.`);
			fetchOrders(); // 목록 새로고침

		} catch (error) {
			console.error('❌ 구매확정 실패:', error);
			alert(error.response?.data?.error || '구매확정에 실패했습니다.');
		} finally {
			setConfirmingOrderId(null);
		}
	};

	// ⭐ 자동확정까지 남은 일수 계산
	const getDaysUntilAutoConfirm = (autoConfirmDate) => {
		if (!autoConfirmDate) return null;
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const confirmDate = new Date(autoConfirmDate);
		const diffTime = confirmDate - today;
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const filteredOrders = activeTab === 'all' 
		? orders 
		: orders.filter(order => order.status === tabs.find(t => t.key === activeTab)?.status);

	const getStatusBadge = (status, order) => {
		const statusMap = {
			'pending': { text: '결제대기', color: '#ffa500' },
			'paid': { text: '결제완료', color: '#4CAF50' },
			'preparing': { text: '배송준비', color: '#2196F3' },
			'shipping': { text: '배송중', color: '#9C27B0' },
			'delivered': { text: '배송완료', color: '#607D8B' },
			'confirmed': { text: '구매확정', color: '#00BCD4' },
			'cancelled': { text: '취소', color: '#f44336' },
			'refund_requested': { text: '환불요청', color: '#FF5722' },
			'refunded': { text: '환불완료', color: '#795548' }
		};

		const badge = statusMap[status] || { text: status, color: '#999' };

		// 배송중/배송완료일 때 클릭 가능
		const isClickable = ['shipping', 'delivered'].includes(status) && order.shipping;

		return (
			<span 
				className={`status-badge ${isClickable ? 'clickable' : ''}`} 
				style={{ background: badge.color, cursor: isClickable ? 'pointer' : 'default' }}
				onClick={() => isClickable && setExpandedShipping(expandedShipping === order.id ? null : order.id)}
			>
				{badge.text} {isClickable && '📦'}
			</span>
		);
	};

	const getCourierName = (code) => {
		const couriers = {
			'cj': 'CJ대한통운',
			'hanjin': '한진택배',
			'lotte': '롯데택배',
			'logen': '로젠택배',
			'kdexp': '경동택배',
			'post': '우체국택배',
			'ems': 'EMS',
			'dhl': 'DHL',
			'fedex': 'FedEx',
			'ups': 'UPS',
			'sf': 'SF익스프레스',
			'cne': 'CNE',
			'yanwen': '연운익스프레스'
		};
		return couriers[code] || code;
	};

	const getTrackingUrl = (courier, trackingNumber) => {
		const urls = {
			'cj': `https://www.cjlogistics.com/ko/tool/parcel/tracking?gnbInvcNo=${trackingNumber}`,
			'hanjin': `https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mession=&wblnumText2=&wblnumText3=&cession=&tession=&wblnum=${trackingNumber}`,
			'lotte': `https://www.lotteglogis.com/home/reservation/tracking/invoice?InvNo=${trackingNumber}`,
			'logen': `https://www.ilogen.com/web/personal/trace/${trackingNumber}`,
			'post': `https://service.epost.go.kr/trace.RetrieveDomRi498.postal?sid1=${trackingNumber}`,
		};
		return urls[courier] || null;
	};

	if (loading) {
		return (
			<div className="orders-page">
				<div className="loading">⏳ 주문내역을 불러오는 중...</div>
			</div>
		);
	}

	return (
		<div className="orders-page">
			<div className="orders-header">
				<h1>주문내역</h1>
			</div>

			<div className="orders-tabs">
				{tabs.map(tab => (
					<button
						key={tab.key}
						className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
						onClick={() => setActiveTab(tab.key)}
					>
						{tab.label}
						<span className="tab-count">
							{tab.key === 'all' 
								? orders.length 
								: orders.filter(o => o.status === tab.status).length}
						</span>
					</button>
				))}
			</div>

			{filteredOrders.length === 0 ? (
				<div className="empty-orders">
					<div className="empty-icon">📦</div>
					<p>주문 내역이 없습니다.</p>
					<button onClick={() => navigate('/')}>쇼핑 계속하기</button>
				</div>
			) : (
				<div className="orders-list">
					{filteredOrders.map(order => (
						<div key={order.id} className="order-card">
							<div className="order-header">
								<div className="order-info">
									<span className="order-date">
										{new Date(order.created_at).toLocaleDateString('ko-KR')}
									</span>
									<span className="order-number">주문번호: {order.id}</span>
								</div>
								{getStatusBadge(order.status, order)}
							</div>

							{/* ⭐ 배송완료 시 자동확정 안내 */}
							{order.status === 'delivered' && order.auto_confirm_date && (
								<div className="auto-confirm-notice">
									<span className="notice-icon">⏰</span>
									<span className="notice-text">
										{getDaysUntilAutoConfirm(order.auto_confirm_date)}일 후 자동 구매확정됩니다
										<span className="notice-date">
											({new Date(order.auto_confirm_date).toLocaleDateString('ko-KR')})
										</span>
									</span>
								</div>
							)}

							{/* ⭐ 구매확정 완료 시 적립 안내 */}
							{order.status === 'confirmed' && order.confirmed_at && (
								<div className="confirmed-notice">
									<span className="notice-icon">✅</span>
									<span className="notice-text">
										{new Date(order.confirmed_at).toLocaleDateString('ko-KR')} 구매확정 완료
										<span className="point-earned">
											+{Math.floor(order.final_amount * 0.01).toLocaleString()}P 적립
										</span>
									</span>
								</div>
							)}

							{/* 배송정보 펼침 */}
							{expandedShipping === order.id && order.shipping && (
								<div className="shipping-info">
									<h4>📦 배송정보</h4>
									
									{/* 해외배송 */}
									{order.shipping.overseas_courier && (
										<div className="shipping-row overseas">
											<span className="shipping-label">🌏 해외배송</span>
											<span className="shipping-courier">{getCourierName(order.shipping.overseas_courier)}</span>
											<span className="shipping-tracking">{order.shipping.overseas_tracking_number}</span>
											{order.shipping.overseas_shipped_at && (
												<span className="shipping-date">
													{new Date(order.shipping.overseas_shipped_at).toLocaleDateString('ko-KR')}
												</span>
											)}
										</div>
									)}
									
									{/* 국내배송 */}
									{order.shipping.courier && (
										<div className="shipping-row domestic">
											<span className="shipping-label">🇰🇷 국내배송</span>
											<span className="shipping-courier">{getCourierName(order.shipping.courier)}</span>
											<span className="shipping-tracking">{order.shipping.tracking_number}</span>
											{getTrackingUrl(order.shipping.courier, order.shipping.tracking_number) && (
												<a 
													href={getTrackingUrl(order.shipping.courier, order.shipping.tracking_number)}
													target="_blank"
													rel="noopener noreferrer"
													className="tracking-link"
												>
													배송조회
												</a>
											)}
										</div>
									)}
								</div>
							)}

							<div className="order-items">
								{order.items?.map((item, idx) => (
									<div key={idx} className="order-item">
										<img 
											src={getImageUrl(item.product_image)} 
											alt={item.product_name}
										/>
										<div className="item-details">
											<h3>{item.product_name}</h3>
											{item.option_name && <p>옵션: {item.option_name}</p>}
											{item.size && <p>사이즈: {item.size}</p>}
											<p className="item-price">
												{Number(item.price).toLocaleString()}원 × {item.quantity}개
											</p>
										</div>
										<div className="item-total">
											{(Number(item.price) * item.quantity).toLocaleString()}원
										</div>
									</div>
								))}
							</div>

							<div className="order-footer">
								<div className="order-total">
									<span>총 결제금액</span>
									<span className="total-amount">
										{Number(order.final_amount).toLocaleString()}원
									</span>
								</div>
								<div className="order-actions">
									<button 
										className="btn-detail"
										onClick={() => navigate(`/orders/${order.id}`)}
									>
										상세보기
									</button>
									{order.status === 'pending' && (
										<button className="btn-pay">결제하기</button>
									)}
									{/* ⭐ 구매확정 버튼 */}
									{order.status === 'delivered' && (
										<button
											className="btn-confirm"
											onClick={() => handleConfirmOrder(order.id)}
											disabled={confirmingOrderId === order.id}
										>
											{confirmingOrderId === order.id ? '처리중...' : '구매확정'}
										</button>
									)}
									{order.status === 'delivered' && (
										<button
											className="btn-review"
											onClick={() => navigate(`/review/write?orderId=${order.id}`)}
										>
											리뷰쓰기
										</button>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}

export default Orders;